import { Header } from "../components/layout/header";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {Button} from "../components/actions/button";
import {CloseSvg, SaveSvg, TrashSvg} from "../components/Svg";
import {Divider, Form, Input, InputNumber, Popconfirm, Radio, Row, Select, TimePicker, Typography} from "antd";
import {whiteSpaceValidation} from "../utils/helpers";
import {
    CompassOutlined,
    DownloadOutlined,
    FieldTimeOutlined,
    MinusCircleOutlined,
    PlusOutlined
} from "@ant-design/icons";
import {ExtraButtons} from "../components/extra-buttons";
import {usePermission} from "../hooks";
import {FileUploaderDrawer, useGetAreas, MassEditTimeDrawer} from "../pages-lib/delivery-areas";
import {useManageItems} from "../pages-lib/delivery-areas/model/use-manage-items";
import moment from "moment";
import {VisibilityProvider} from "../components/visibility-provider";
const { Title } = Typography;

const colorZones = [
    {
        label: "Зеленый",
        value: "#1bad03"
    },
    {
        label: "Оранжевый",
        value: "#ff931e"
    },
    {
        label: "Красный",
        value: "#FF0000"
    },
]

export default function DeliveryAreas() {
    const { hasPermission } = usePermission()
    const canEditZones = hasPermission("delivery-areas.edit")
    const canEditHurryUpTime = hasPermission("delivery-areas.edit-hurry-up-time")
    const canUploadFile = hasPermission("delivery-areas.upload")
    const canExportMap = hasPermission("delivery-areas.export")
    const canCreateZone = hasPermission("delivery-areas.create")
    const canShowCreateButton = hasPermission("delivery-areas.button-create")
    const canDeleteZone = hasPermission("delivery-areas.delete")
    // Полный edit: можно править все поля. Иначе если есть только edit-hurry-up-time — частичный.
    // Если нет ни того ни другого — read-only.
    const isReadOnly = !canEditZones && !canEditHurryUpTime
    const isHurryUpOnly = !canEditZones && canEditHurryUpTime

    const [fileUploaderDrawer, setFileUploaderDrawer] = useState(false);
    const [massEditHurryUpTime, setMassEditHurryUpTime] = useState(false);
    const [map, setMap] = useState(null);
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);
    const [pointMarkers, setPointMarkers] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [zoneData, setZoneData] = useState({ name: "", description: "" });

    const [form] = Form.useForm();

    const { items, refetch: getItems, options: { pointOptions}} = useGetAreas();
    const { onSave, onSendMass, onDownloadMap } = useManageItems(getItems);

    useEffect(() => {
        if (window.ymaps) {
            window.ymaps.ready(initMap);
        }
    }, []);

    const initMap = () => {
        const mapInstance = new window.ymaps.Map("yandex-map", {
            center: [59.93863, 30.31413],
            zoom: 10,
            controls: ["zoomControl", "fullscreenControl"],
        });

        setMap(mapInstance);
    };

    useEffect(() => {
        if (!selectedZone) return;

        // Начинаем редактирование новой зоны (только если есть полное право редактирования)
        if (!isReadOnly && !isHurryUpOnly) {
            selectedZone.editor.startEditing();
        }
        selectedZone.options.set({
            strokeColor: "#0000FF",
            strokeWidth: 4,
            strokeOpacity: 1,
        });

        return () => {
            // Сбрасываем стиль предыдущей зоны при смене selectedZone
            selectedZone.editor.stopEditing();
            selectedZone.options.set({
                strokeColor: selectedZone.__originalStyle.strokeColor,
                strokeWidth: selectedZone.__originalStyle.strokeWidth,
                strokeOpacity: selectedZone.__originalStyle.strokeOpacity,
                fillColor: selectedZone.__originalStyle.fillColor,
                fillOpacity: selectedZone.__originalStyle.fillOpacity,
            });
        };
    }, [selectedZone]);

    useEffect(() => {
        if (map && items.length > 0) {
            items.forEach(point => {
                if (point.deliveryZones.length > 0) {
                    if (point.latitude && point.longitude) {
                        const placemark = new window.ymaps.Placemark(
                            [point.latitude, point.longitude],
                            {
                                hintContent: point.shortName?.[0]?.content || "Точка",
                                balloonContent: `
                                <strong>${point.name?.[0]?.content || "Точка доставки"}</strong><br />
                                ${point.addressString || ""}
                            `,
                            },
                            {
                                preset: "islands#blueDotIcon",
                                draggable: false,
                            }
                        );

                        placemark.__isStatic = true;
                        placemark.__pointId = point.id;

                        map.geoObjects.add(placemark);
                    }

                    point.deliveryZones.forEach(zone => {
                        if (!zone.geojson) return;

                        let parsedGeojson;
                        try {
                            parsedGeojson = JSON.parse(zone.geojson);
                        } catch (e) {
                            console.warn("❌ Ошибка парсинга geojson:", zone.geojson);
                            return;
                        }

                        if (parsedGeojson?.geometry?.type === "Polygon") {
                            const fixedCoordinates = parsedGeojson.geometry.coordinates.map(ring =>
                                ring.map(([lon, lat]) => [lat, lon])
                            );
                            const fillColor = parsedGeojson.properties?.fillColor || "#1bad03";

                            const polygonStyles = {
                                strokeColor: fillColor,
                                strokeWidth: 3,
                                fillColor,
                                fillOpacity: 0.3,
                            };

                            const polygon = new window.ymaps.Polygon(
                                fixedCoordinates,
                                {
                                    hintContent: `
                                      <div>
                                        <strong>${parsedGeojson.properties?.name || "Зона доставки"}</strong><br />
                                        ${parsedGeojson.properties?.description || ""}
                                      </div>
                                    `,
                                    balloonContent: `
                                      <div>
                                        <strong>${parsedGeojson.properties?.name || "Зона доставки"}</strong><br />
                                        ${parsedGeojson.properties?.description || ""}
                                      </div>
                                    `,
                                },
                                {
                                    ...polygonStyles,
                                    draggable: false,
                                }
                            );

                            polygon.__uuid = zone.uuid;
                            polygon.__pointId = point.id;
                            polygon.__conditions = zone.conditions;
                            polygon.__mode = "update";

                            polygon.__originalStyle = { ...polygonStyles };

                            polygon.events.add("click", () => {
                                resetZoneSelection();

                                if (!isReadOnly && !isHurryUpOnly) {
                                    polygon.editor.startEditing();
                                }
                                polygon.options.set({
                                    strokeColor: "#0000FF",
                                    strokeWidth: 4,
                                    strokeOpacity: 1,
                                });

                                form.setFieldsValue({
                                    name: parsedGeojson.properties?.name || "",
                                    description: parsedGeojson.properties?.description || "",
                                    pointId: point.id,
                                    fillColor: polygon.options.get("fillColor"),
                                    isActive: zone.isActive,
                                    conditions: zone.conditions,
                                    timeForHurryUp: parsedGeojson.properties?.timeForHurryUp
                                        ? moment(parsedGeojson.properties.timeForHurryUp, "HH:mm:ss")
                                        : null,
                                    currentTimeForHurryUp: parsedGeojson.properties?.currentTimeForHurryUp
                                        ? moment(parsedGeojson.properties.currentTimeForHurryUp, "HH:mm:ss")
                                        : null,
                                    timeForKitchen: parsedGeojson.properties?.timeForKitchen
                                        ? moment(parsedGeojson.properties.timeForKitchen, "HH:mm:ss")
                                        : null,
                                    currentTimeForKitchen: parsedGeojson.properties?.currentTimeForKitchen
                                        ? moment(parsedGeojson.properties.currentTimeForKitchen, "HH:mm:ss")
                                        : null,
                                    isActiveKitchenHurryUpTime: parsedGeojson.properties?.isActiveKitchenHurryUpTime !== 0 ?? true,
                                    isActiveHurryUpTime: parsedGeojson.properties?.isActiveHurryUpTime !== 0 ?? true,
                                });

                                setZoneData({
                                    name: parsedGeojson.properties?.name || "",
                                    description: parsedGeojson.properties?.description || "",
                                });

                                setSelectedZone(polygon);
                                setIsEditing(true);
                            });

                            map.geoObjects.add(polygon);
                        }
                    });
                }
            });
        }
    }, [map, items]);



    const fixCoordinates = (coords) => {
        return coords.map(coord =>
            Array.isArray(coord[0])
                ? fixCoordinates(coord)
                : [coord[1], coord[0]]
        );
    };

    const handlePointChange = (pointId, zone = selectedZone) => {
        if (!map || !pointId || !zone) return;

        const point = items.find((p) => p.id === pointId);
        if (!point || !point.latitude || !point.longitude) {
            return;
        }

        // Удаляем предыдущую временную точку, связанную с этой зоной
        map.geoObjects.each((geoObject) => {
            if (
                geoObject.__isTemporary &&
                (geoObject.__zoneId === zone.__zoneId || geoObject.__uuid === zone.__uuid)
            ) {
                map.geoObjects.remove(geoObject);
            }
        });

        // Создаём новую временную точку (Placemark)
        const placemark = new window.ymaps.Placemark(
            [point.latitude, point.longitude],
            {
                hintContent: point.shortName?.[0]?.content || "Точка",
                balloonContent: `
                    <strong>${point.name?.[0]?.content || "Точка доставки"}</strong><br />
                    ${point.addressString || ""}
                `,
            },
            {
                preset: "islands#blueDotIcon",
                draggable: false,
            }
        );

        placemark.__isTemporary = true;
        placemark.__zoneId = zone.__zoneId || zone.__uuid;

        map.geoObjects.add(placemark);
    };



    const normalizeCoordinatesToGeoJson = (coords) => {
        return coords.map(polygon =>
            polygon.map(([lat, lon]) => [lon, lat])
        );
    };

    const getGeoJsonFromPolygon = (polygon, uuid) => {
        const rawCoords = polygon.geometry.getCoordinates();
        const geoJsonCoords = normalizeCoordinatesToGeoJson(rawCoords);

        return {
            type: 'Feature',
            id: uuid,
            geometry: {
                type: 'Polygon',
                coordinates: geoJsonCoords,
            },
            properties: {
                name: form.getFieldValue('name'),
                description: form.getFieldValue('description'),
                fillColor: form.getFieldValue('fillColor'),
                timeForHurryUp: form.getFieldValue('timeForHurryUp')?.format?.("HH:mm:ss") || null,
                currentTimeForHurryUp: form.getFieldValue('currentTimeForHurryUp')?.format?.("HH:mm:ss") || null,
                timeForKitchen: form.getFieldValue('timeForKitchen')?.format?.("HH:mm:ss") || null,
                currentTimeForKitchen: form.getFieldValue('currentTimeForKitchen')?.format?.("HH:mm:ss") || null,
                isActiveKitchenHurryUpTime: form.getFieldValue('isActiveKitchenHurryUpTime') ? 1 : 0,
                isActiveHurryUpTime: form.getFieldValue('isActiveHurryUpTime') ? 1 : 0,
            },
        };
    };


    async function onSubmit(values) {
        if (!selectedZone) return;

        const eventType = selectedZone?.__mode === 'create' ? 'create' : 'update';
        const uuid = selectedZone?.__uuid || crypto.randomUUID();

        const resultPayload = {
            uuid,
            eventType,
            pointId: values.pointId,
            color: values.fillColor,
            isActive: values.isActive ? 1 : 0,
            geojson: JSON.stringify(getGeoJsonFromPolygon(selectedZone, uuid)),
            conditions: values.conditions.map((item) => ({
                deliveryPrice: Number(item.deliveryPrice),
                totalSum: Number(item.totalSum),
            })),
        };

        const requestBody = {
            elements: [resultPayload],
        };

        try {
            await onSave(requestBody)


            setIsEditing(false);
        } catch (e) {
        }
    }

    async function onDeleteZone(id) {
        if (!selectedZone) return;

        const uuid = selectedZone.__uuid;
        const pointId = selectedZone.__pointId;

        const deletePayload = {
            uuid,
            eventType: 'delete',
            pointId,
            color: selectedZone.options.get('fillColor'),
            isActive: false,
            geojson: null,
            conditions: [],
        };

        const requestBody = {
            elements: [deletePayload],
        };

        try {
            await onSave(requestBody);
            map.geoObjects.remove(selectedZone);
            setSelectedZone(null);
            setIsEditing(false);
        } catch (e) {
            console.error("Ошибка при удалении зоны:", e);
        }
    }

    const resetZoneSelection = () => {
        if (selectedZone) {
            selectedZone.editor.stopEditing();
            selectedZone.options.set({
                strokeColor: selectedZone.__originalStyle.strokeColor,
                strokeWidth: selectedZone.__originalStyle.strokeWidth,
                strokeOpacity: selectedZone.__originalStyle.strokeOpacity,
                fillColor: selectedZone.__originalStyle.fillColor,
                fillOpacity: selectedZone.__originalStyle.fillOpacity,
            });

            selectedZone.balloon.close();
        }
    };

    const createNewZone = () => {
        if (!map) return;

        const uuid = crypto.randomUUID();

        const newPolygon = new window.ymaps.Polygon(
            [[[59.950, 30.300], [59.970, 30.350], [59.920, 30.400], [59.950, 30.300]]], // Простой треугольник
            {
                hintContent: "Новая зона",
                balloonContent: "Описание новой зоны",
            },
            {
                strokeColor: "#0000FF",
                strokeWidth: 3,
                fillColor: "#1bad03",
                fillOpacity: 0.3,
                draggable: true,
            }
        );
        newPolygon._isNew = true;
        newPolygon.__mode = 'create'

        newPolygon.__originalStyle = {
            strokeColor: "#0000FF",
            strokeWidth: 3,
            fillColor: "#1bad03",
            fillOpacity: 0.3,
        };

        map.geoObjects.add(newPolygon);

        setZones([...zones, newPolygon]);

        setSelectedZone(newPolygon);
        setIsEditing(true);

        setZoneData({
            name: "Новая зона",
            description: "Описание новой зоны",
        });

        form.setFieldsValue({
            name: "Новая зона",
            description: "Описание новой зоны",
            fill: "#1bad03",
        });

        newPolygon.editor.startEditing();
    };

    const handleColorChange = (value) => {
        if (selectedZone) {
            selectedZone.options.set({
                fillColor: value,
            });
        }
    };

    async function onAddAreaInFile({ pointId, geo }) {
        if (!map || !geo?.features || geo.features.length === 0) return;

        const feature = geo.features[0];
        if (feature.geometry?.type !== "Polygon") {
            console.warn("Поддерживаются только полигоны");
            return;
        }

        const coords = feature.geometry.coordinates.map(ring =>
            ring.map(([lon, lat]) => [lat, lon])
        );

        const rawDescription = feature.properties?.description;
        const description = typeof rawDescription === "object" ? rawDescription.value : rawDescription;
        const fillColor = feature.properties?.fill || "#1bad03";
        const name = description || "Зона без имени";

        const uuid = crypto.randomUUID();

        const newPolygon = new window.ymaps.Polygon(
            coords,
            {
                hintContent: `
                  <div>
                    <strong>${name}</strong><br />
                    ${description || ""}
                  </div>
                `,
                balloonContent: `
                  <div>
                    <strong>${name}</strong><br />
                    ${description || ""}
                  </div>
                `,
            },
            {
                strokeColor: fillColor,
                strokeWidth: 3,
                fillColor,
                fillOpacity: 0.3,
                draggable: true,
            }
        );

        newPolygon.__uuid = uuid;
        newPolygon.__zoneId = uuid;
        newPolygon.__mode = "create";
        newPolygon.__pointId = pointId;
        newPolygon.__originalStyle = {
            strokeColor: fillColor,
            strokeWidth: 3,
            fillColor,
            fillOpacity: 0.3,
        };

        map.geoObjects.add(newPolygon);

        setZoneData({ name, description });
        setSelectedZone(null);

        setSelectedZone(newPolygon);
        setIsEditing(true);
        newPolygon.editor.startEditing();

        form.setFieldsValue({
            name,
            description,
            pointId,
            fillColor,
            timeForHurryUp: null,
            currentTimeForHurryUp: null,
            timeForKitchen: null,
            currentTimeForKitchen: null,
            isActive: true,
            conditions: [],
        });

        handlePointChange(pointId, newPolygon);
    }

    const id = "new";

    return (
        <>
            <Header
                title={"Зоны доставки"}
                extra={
                    <ExtraButtons
                        onCreate={canShowCreateButton && canCreateZone ? () => createNewZone() : undefined}
                        //onFilter={() => setFilterDrawerState(!filterDrawerState)}
                        // onSort(() => setSortableDrawerState(!sortableDrawerState)}
                        titles={{ onCreate: "Добавить Зону" }}
                    />
                }
            />

            <ButtonsRowFlex>
                {canUploadFile && (
                    <StyledButton
                        color={"#808080"}
                        onClick={() => setFileUploaderDrawer(true)}
                        icon={<CompassOutlined style={{ fontSize: "16px" }} />}
                    >
                        {"Загрузить зону файлом"}
                    </StyledButton>
                )}

                {(canEditZones || canEditHurryUpTime) && (
                    <StyledButton
                        color={"#808080"}
                        onClick={() => setMassEditHurryUpTime(true)}
                        icon={<FieldTimeOutlined style={{ fontSize: "16px" }} />}
                    >
                        {"Массовое управление временем"}
                    </StyledButton>
                )}

                {canExportMap && (
                    <StyledButton
                        color={"#808080"}
                        onClick={onDownloadMap}
                        icon={<DownloadOutlined style={{ fontSize: "16px" }} />}
                    >
                        {"Выгрузить карту"}
                    </StyledButton>
                )}
            </ButtonsRowFlex>


            <MapWrap>
                <Map id="yandex-map" />
                {isEditing && (
                    <EditPanel>
                        <Title level={4}>
                            {selectedZone?.__mode === 'create'
                                ? 'Создание зоны'
                                : `${isReadOnly ? 'Просмотр' : 'Редактирование'}: ${form.getFieldValue('name') || 'Без названия'}`}
                        </Title>
                        <FormEdit
                            key={`form_${zoneData.description}`}
                            form={form}
                            layout={"vertical"}
                            onFinish={(values) => onSubmit(values)}
                            disabled={isReadOnly}
                        >
                            <StyledFormItem
                                label={"Название"}
                                key={id + "_name"}
                                name={"name"}
                                rules={[
                                    {
                                        required: true,
                                        message: "Введите название!",
                                    },
                                ]}
                            >
                                <Input style={{ width: "100%" }} maxLength={256} disabled={isHurryUpOnly} />
                            </StyledFormItem>
                            <StyledFormItem
                                label={"Описание"}
                                key={id + "_description"}
                                name={"description"}
                                rules={[
                                    {
                                        required: true,
                                        message: "Введите описание!",
                                    },
                                ]}
                            >
                                <Input style={{ width: "100%" }} maxLength={256} disabled={isHurryUpOnly} />
                            </StyledFormItem>
                            <StyledFormItem
                                key={id + "_pointId"}
                                label={"Точка"}
                                name={"pointId"}
                                rules={[
                                    {
                                        required: true,
                                        message: "Выберите точку!",
                                    },
                                ]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={pointOptions}
                                    placeholder="Начните вводить название..."
                                    disabled={isHurryUpOnly}
                                />
                            </StyledFormItem>
                            <StyledFormItem
                                key={id + "_fillColor"}
                                label={"Цвет"}
                                name={"fillColor"}
                                rules={[
                                    {
                                        required: true,
                                        message: "Выберите категорию!",
                                    },
                                ]}
                            >
                                <Select
                                    options={colorZones}
                                    onChange={handleColorChange}
                                    disabled={isHurryUpOnly}
                                />
                            </StyledFormItem>
                            <StyledFormItem
                                key={id + "_isActive"}
                                label={"Зона активна"}
                                name={"isActive"}
                                initialValue={form.isActive || true}
                                rules={[
                                    {
                                        required: true,
                                        message: "Выберите вариант!",
                                    },
                                ]}
                            >
                                <Radio.Group buttonStyle="solid" value={form.isActive || true} disabled={isHurryUpOnly}>
                                    <Radio.Button value={true}>Да</Radio.Button>
                                    <Radio.Button value={false}>Нет</Radio.Button>
                                </Radio.Group>
                            </StyledFormItem>
                            <Title level={5}>Интервалы</Title>
                            <Divider orientation={"left"}>Поскорее</Divider>
                            <StyledFormItem
                                label={"Интервал для 'поскорее'"}
                                key={"timeForHurryUp"}
                                name={"timeForHurryUp"}
                                rules={[
                                    {
                                        required: true,
                                        message: "Выберите время!",
                                    },
                                ]}
                            >
                                <TimePicker style={{ width: "100%" }} format="HH:mm:ss" disabled={isHurryUpOnly} />
                            </StyledFormItem>
                            <StyledFormItem
                                key={"currentTimeForHurryUp"}
                                label={"Текущий интервал для 'поскорее'"}
                                name={"currentTimeForHurryUp"}
                                rules={[{ required: true, message: "Выберите время!" }]}
                            >
                                <TimePicker style={{ width: "100%" }} format="HH:mm:ss" />
                            </StyledFormItem>
                            <StyledFormItem
                                key={id + "_isActiveHurryUpTime"}
                                label={"Текущий интервал для поскорее включен"}
                                name={"isActiveHurryUpTime"}
                                initialValue={form.isActiveHurryUpTime || true}
                                rules={[
                                    {
                                        required: true,
                                        message: "Выберите вариант!",
                                    },
                                ]}
                            >
                                <Radio.Group buttonStyle="solid" value={form.isActiveHurryUpTime || true} disabled={isHurryUpOnly}>
                                    <Radio.Button value={true}>Да</Radio.Button>
                                    <Radio.Button value={false}>Нет</Radio.Button>
                                </Radio.Group>
                            </StyledFormItem>
                            <Divider orientation={"left"}>Для кухни</Divider>
                            <StyledFormItem
                                key={"timeForKitchen"}
                                label={"Интервал кухни"}
                                name={"timeForKitchen"}
                                rules={[{ required: true, message: "Выберите время!" }]}
                            >
                                <TimePicker style={{ width: "100%" }} format="HH:mm:ss" disabled={isHurryUpOnly} />
                            </StyledFormItem>


                            <StyledFormItem
                                key={"currentTimeForKitchen"}
                                label={"Текущий интервал кухни"}
                                name={"currentTimeForKitchen"}
                                rules={[{ required: true, message: "Выберите время!" }]}
                            >
                                <TimePicker style={{ width: "100%" }} format="HH:mm:ss" />
                            </StyledFormItem>
                            <StyledFormItem
                                key={id + "_isActiveKitchenHurryUpTime"}
                                label={"Текущий интервал для кухни включен"}
                                name={"isActiveKitchenHurryUpTime"}
                                initialValue={form.isActiveKitchenHurryUpTime || true}
                                rules={[
                                    {
                                        required: true,
                                        message: "Выберите вариант!",
                                    },
                                ]}
                            >
                                <Radio.Group buttonStyle="solid" value={form.isActiveKitchenHurryUpTime || true} disabled={isHurryUpOnly}>
                                    <Radio.Button value={true}>Да</Radio.Button>
                                    <Radio.Button value={false}>Нет</Radio.Button>
                                </Radio.Group>
                            </StyledFormItem>

                            <Divider orientation={"left"}>Условия доставки</Divider>
                            <Form.List name="conditions">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...field }, index) => (
                                            <React.Fragment key={`RowTitle_${index}`}>
                                                <RowTitle level={5}>
                                                    Условие {index + 1}{" "}
                                                    {fields.length > 0 && !isReadOnly && !isHurryUpOnly ? (
                                                        <MinusCircleOutlined
                                                            className="dynamic-delete-button"
                                                            style={{ marginLeft: "12px" }}
                                                            onClick={() => {
                                                                remove(name);
                                                            }}
                                                        />
                                                    ) : null}
                                                </RowTitle>
                                                <Form.Item required={false} key={key}>
                                                    <RowFlex>
                                                        <StyledFormItemNoMargin
                                                            {...field}
                                                            key={id + "_deliveryPrice"}
                                                            label={"Стоимость доставки, ₽"}
                                                            name={[name, "deliveryPrice"]}
                                                            style={{ width: "100%" }}
                                                            rules={[
                                                                {
                                                                    message: "Введите сумму!",
                                                                    required: true
                                                                },
                                                                {
                                                                    validator: whiteSpaceValidation,
                                                                },
                                                            ]}
                                                        >
                                                            <Input style={{ width: "100%" }} type={"text"} placeholder={"Сумма"} disabled={isHurryUpOnly} />
                                                        </StyledFormItemNoMargin>
                                                        <StyledFormItemNoMargin
                                                            {...field}
                                                            key={id + "_totalSum"}
                                                            label={"Заказ от суммы, ₽"}
                                                            name={[name, "totalSum"]}
                                                            style={{ width: "100%" }}
                                                            rules={[
                                                                {
                                                                    message: "Введите сумму!",
                                                                    required: true
                                                                },
                                                                {
                                                                    validator: whiteSpaceValidation,
                                                                },
                                                            ]}
                                                        >
                                                            <InputNumber min={0} style={{ width: "100%" }} type={"text"} placeholder={"Сумма"} disabled={isHurryUpOnly} />
                                                        </StyledFormItemNoMargin>
                                                    </RowFlex>
                                                </Form.Item>
                                            </React.Fragment>
                                        ))}
                                        {!isReadOnly && !isHurryUpOnly && (
                                            <Form.Item>
                                                <Button
                                                    type="dashed"
                                                    onClick={() => add()}
                                                    style={{ width: "100%" }}
                                                    icon={<PlusOutlined />}
                                                >
                                                    Добавить условие
                                                </Button>
                                            </Form.Item>
                                        )}
                                    </>
                                )}
                            </Form.List>
                            <ButtonRow>
                                {!isReadOnly && (
                                    <Button
                                        style={{ width: "100%" }}
                                        icon={<SaveSvg />}
                                        type="primary"
                                        htmlType={"submit"}
                                    >
                                        Сохранить
                                    </Button>
                                )}
                                <Button
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        resetZoneSelection();
                                        setIsEditing(false);
                                    }}
                                    icon={<CloseSvg />}
                                    htmlType={"button"}
                                >
                                    Закрыть
                                </Button>
                                {canDeleteZone && !isHurryUpOnly && (
                                    <Popconfirm
                                        placement="left"
                                        title="Действительно хотите удалить зону?"
                                        onConfirm={() => onDeleteZone(selectedZone.__uuid)}
                                        onCancel={() => stopPropagation}
                                    >
                                        <Button
                                            danger
                                            style={{ width: "100%" }}
                                            onClick={stopPropagation}
                                            icon={<TrashSvg />}
                                            htmlType={"button"}
                                        >
                                            Удалить зону
                                        </Button>
                                    </Popconfirm>
                                )}

                            </ButtonRow>
                        </FormEdit>
                    </EditPanel>
                )}
            </MapWrap>

            <VisibilityProvider visible={fileUploaderDrawer}>
                <FileUploaderDrawer
                    title={"Загрузика зоны файлом"}
                    onClose={() => setFileUploaderDrawer(false)}
                    onSave={onAddAreaInFile}
                    pointOption={pointOptions}
                />
            </VisibilityProvider>

            <VisibilityProvider visible={massEditHurryUpTime}>
                <MassEditTimeDrawer
                    title={"Массовое управление интервалом 'поскорее' на всех зонах точки"}
                    onClose={() => setMassEditHurryUpTime(false)}
                    onSave={onSendMass}
                    pointOption={pointOptions}
                    withKitchenType
                />
            </VisibilityProvider>
        </>
    );
}

const MapWrap = styled.div`
  width: 100%;
  height: calc(100% - 100px);
  position: relative;
  border-radius: 6px;
  overflow: hidden;
`;

const Map = styled.div`
  width: 100%;
  height: 100%;
`;

const EditPanel = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 400px;
  max-height: calc(100% - 20px);
  overflow: auto;
`;

const FormEdit = styled(Form)``

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const StyledFormItemNoMargin = styled(Form.Item)`
  margin-bottom: 0;
`

const ButtonRow = styled(Row)`
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;
  gap: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`

const RowFlex = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
`

const ButtonsRowFlex = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
`
const RowTitle = styled(Title)`
  margin-bottom: 20px;
`

const StyledButton = styled(Button)`
    margin: 10px 0 20px 0 ;
`;

const stopPropagation = (e) => {
    e?.stopPropagation()
}
